import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, message, Popconfirm, Select, Space, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  batchUpdateContentItemOrder,
  deleteContentItem,
  listContentChannels,
  listContentItems,
  publishContentItem,
  unpublishContentItem,
  updateContentItem,
} from '../../../services/content';
import { ContentChannel, ContentItem } from '../../../types/content';
import { channelKeyToRouteSlug } from '../../../config/resourceHub';

const statusLabelMap: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
  archived: '已归档',
};

const statusPillClassMap: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-amber-100 text-amber-700',
};

const extractErrorText = (error: any): string => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.detail) return String(data.detail);
  if (typeof data === 'object' && data) {
    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (firstValue) return String(firstValue);
  }
  return '操作失败';
};

const buildPublicPath = (row: ContentItem): string => {
  if (row.channel_key === 'home_announcements') return `/announcements/${row.id}`;
  if (row.channel_key === 'general_content') {
    const slug = String(row.extra_json?.slug || '').trim().toLowerCase() || `gc-${row.id}`;
    return `/content/${slug}`;
  }
  const routeSlug = channelKeyToRouteSlug(row.channel_key);
  if (routeSlug) {
    return row.slug ? `/resources/${routeSlug}/${row.slug}` : '';
  }
  return '';
};

const ContentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ContentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [channels, setChannels] = useState<ContentChannel[]>([]);
  const [keyword, setKeyword] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [channelId, setChannelId] = useState<number | undefined>(undefined);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const sortingEnabled = Boolean(channelId);

  const channelOptions = useMemo(
    () => channels.map((c) => ({ label: `${c.name} (${c.key})`, value: c.id })),
    [channels]
  );

  const loadChannels = useCallback(async () => {
    try {
      const data = await listContentChannels();
      setChannels(data);
    } catch (error) {
      message.error('加载内容频道失败');
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listContentItems({
        page,
        page_size: pageSize,
        channel: channelId,
        status,
        keyword: keyword || undefined,
      });
      setRows(data.results);
      setTotal(data.count);
    } catch (error) {
      message.error('加载内容列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, channelId, status, keyword]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setKeyword(keywordInput.trim());
      setPage(1);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [keywordInput]);

  const handlePublishToggle = async (row: ContentItem) => {
    try {
      if (row.status === 'published') {
        await unpublishContentItem(row.id);
        message.success('已撤回为草稿');
      } else {
        await publishContentItem(row.id);
        message.success('发布成功');
      }
      loadRows();
    } catch (error) {
      message.error(extractErrorText(error));
    }
  };

  const handleArchiveToggle = async (row: ContentItem) => {
    try {
      if (row.status === 'archived') {
        await updateContentItem(row.id, { status: 'draft' });
        message.success('已取消归档，状态恢复为草稿');
      } else {
        await updateContentItem(row.id, { status: 'archived' });
        message.success('归档成功');
      }
      loadRows();
    } catch (error) {
      message.error(extractErrorText(error));
    }
  };

  const handleDelete = async (row: ContentItem) => {
    try {
      await deleteContentItem(row.id);
      message.success('删除成功');
      loadRows();
    } catch (error) {
      message.error(extractErrorText(error));
    }
  };

  const handleCopyPublicUrl = async (row: ContentItem) => {
    const path = buildPublicPath(row);
    if (!path) {
      message.info('该频道暂无可用前台URL');
      return;
    }
    const absolute = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(absolute);
      message.success('前台URL已复制');
    } catch {
      message.error('复制失败，请手动复制');
    }
  };

  const handleSortBlur = async (row: ContentItem, value: number) => {
    if (Number.isNaN(value) || value === row.sort_order) return;
    try {
      await batchUpdateContentItemOrder([{ id: row.id, sort_order: value }]);
      message.success('排序已更新');
      loadRows();
    } catch (error) {
      message.error('排序更新失败');
    }
  };

  const persistRowOrder = async (nextRows: ContentItem[]) => {
    const base = (page - 1) * pageSize;
    const orders = nextRows.map((row, idx) => ({
      id: row.id,
      sort_order: base + idx + 1,
    }));
    await batchUpdateContentItemOrder(orders);
  };

  const handleDropRow = async (targetId: number) => {
    if (!sortingEnabled) {
      setDraggingId(null);
      return;
    }
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      return;
    }
    const fromIndex = rows.findIndex((x) => x.id === draggingId);
    const toIndex = rows.findIndex((x) => x.id === targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggingId(null);
      return;
    }

    const nextRows = [...rows];
    const [moved] = nextRows.splice(fromIndex, 1);
    nextRows.splice(toIndex, 0, moved);
    setRows(nextRows);
    setDraggingId(null);

    try {
      await persistRowOrder(nextRows);
      message.success('拖拽排序已保存');
      loadRows();
    } catch (error) {
      message.error('拖拽排序保存失败');
      loadRows();
    }
  };

  const columns: ColumnsType<ContentItem> = [
    {
      title: '标题',
      dataIndex: 'title',
      ellipsis: true,
    },
    {
      title: '频道',
      dataIndex: 'channel_name',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.channel_name}</span>
          <span className="text-xs text-gray-500">{row.channel_key}</span>
        </Space>
      ),
    },
    {
      title: '形式',
      dataIndex: 'content_type',
      width: 90,
      render: (value: string) => {
        const map: Record<string, string> = { article: '文章', photo_set: '图集', video: '视频' };
        return value && value !== 'article' ? map[value] || value : <span className="text-gray-400">-</span>;
      },
    },
    {
      title: '来源',
      dataIndex: 'is_original',
      width: 80,
      render: (value: boolean, row: ContentItem) =>
        row.channel_key?.startsWith('resource_') ? (
          value === false ? <span className="text-orange-500">转载</span> : <span className="text-gray-500">原创</span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: '前台URL',
      key: 'public_url',
      width: 300,
      render: (_, row) => {
        const path = buildPublicPath(row);
        if (!path) return <span className="text-gray-400">-</span>;
        return (
          <Space size={6}>
            <a
              href={path}
              target="_blank"
              rel="noopener noreferrer"
              className="max-w-[190px] truncate text-blue-600 hover:text-blue-700 hover:underline"
              title={path}
            >
              {path}
            </a>
            <Button size="small" onClick={() => handleCopyPublicUrl(row)}>
              <CopyOutlined />
            </Button>
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      render: (value: string) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
            statusPillClassMap[value] || 'bg-gray-100 text-gray-700'
          }`}
        >
          {statusLabelMap[value] || value}
        </span>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      width: 120,
      render: (value: number, row) => (
        <Input
          key={`${row.id}-${value}`}
          type="number"
          defaultValue={value}
          disabled={!sortingEnabled}
          onBlur={(e) => handleSortBlur(row, Number(e.target.value))}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 190,
      render: (value: string) => <span>{value ? value.replace('T', ' ').slice(0, 19) : '-'}</span>,
    },
    {
      title: (
        <Space size={4}>
          <span>操作</span>
          <Tooltip
            title={
              <div>
                <div>撤回：仅对已发布内容生效，状态变更为草稿。</div>
                <div>归档：将内容标记为已归档，不在前台展示。</div>
                <div>取消归档：将内容恢复为草稿，可再次发布。</div>
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: '#1677ff' }} />
          </Tooltip>
        </Space>
      ),
      key: 'actions',
      width: 420,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => navigate(`/dashboard/admin/content/edit/${row.id}`)}>
            编辑
          </Button>
          <Button size="small" onClick={() => navigate(`/dashboard/admin/content/preview/${row.id}`)}>
            预览页
          </Button>
          <Button size="small" onClick={() => handleArchiveToggle(row)}>
            {row.status === 'archived' ? '取消归档' : '归档'}
          </Button>
          <Button
            size="small"
            type="primary"
            ghost
            disabled={row.status === 'archived'}
            title={row.status === 'archived' ? '请先取消归档，再执行发布' : undefined}
            onClick={() => handlePublishToggle(row)}
          >
            {row.status === 'published' ? '撤回' : '发布'}
          </Button>
          <Popconfirm
            title="确认彻底删除该内容？"
            description="删除后不可恢复，将从前台与管理端列表中移除。"
            okText="确认删除"
            cancelText="取消"
            onConfirm={() => handleDelete(row)}
          >
            <Button size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-800">内容管理</h1>
        <p className="mt-1 text-gray-600">管理首页轮播图、公告栏、FAQ 与系统使用指南内容</p>
      </div>

      <div className="mb-4">
        <Space wrap>
          <Select
            allowClear
            style={{ width: 230 }}
            placeholder="按频道筛选"
            options={channelOptions}
            value={channelId}
            onChange={(v) => {
              setChannelId(v);
              setPage(1);
            }}
          />
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="按状态筛选"
            options={[
              { label: '草稿', value: 'draft' },
              { label: '已发布', value: 'published' },
              { label: '已归档', value: 'archived' },
            ]}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          />
          <Input
            style={{ width: 280 }}
            placeholder="搜索标题/摘要/正文"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
          />
          <Button
            type="primary"
            onClick={() => {
              setKeyword(keywordInput.trim());
              setPage(1);
            }}
          >
            搜索
          </Button>
          <Button
            onClick={() => {
              setKeyword('');
              setKeywordInput('');
              setStatus(undefined);
              setChannelId(undefined);
              setPage(1);
            }}
          >
            重置
          </Button>
          <Button
            type="primary"
            style={{ marginLeft: 30 }}
            icon={<PlusOutlined />}
            onClick={() => navigate('/dashboard/admin/content/edit/new')}
          >
            新建内容
          </Button>
        </Space>
      </div>

      {!sortingEnabled ? (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="请先选择频道后再进行拖拽排序或手动修改排序值"
        />
      ) : null}

      <Table<ContentItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        rowClassName={(record) => (record.id === draggingId ? 'opacity-60' : '')}
        onRow={(record) => ({
          draggable: sortingEnabled,
          onDragStart: () => setDraggingId(record.id),
          onDragOver: (e) => {
            if (!sortingEnabled) return;
            e.preventDefault();
          },
          onDrop: async (e) => {
            if (!sortingEnabled) return;
            e.preventDefault();
            await handleDropRow(record.id);
          },
          onDragEnd: () => setDraggingId(null),
          style: { cursor: sortingEnabled ? 'move' : 'not-allowed' },
        })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (all, range) => `第${range[0]}-${range[1]}条 共${all}条记录`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default ContentListPage;

